import { useState } from "react";
import Card from "components/card";
import FileSelector from "components/fields/FileSelector";
import { addProductImage, deleteProductImage } from "interfaces/products";
import { toast } from "react-toastify";
import { MdClose, MdCloudUpload, MdStar } from "react-icons/md";

const ProductImageWidget = (props: {
  productId: string,
  imageUrl?: string,
  imageUrls?: string[],
  onImagesUpdate?: () => void
}) => {
  const { productId, imageUrl, imageUrls = [], onImagesUpdate } = props;
  const [uploadingImage, setUploadingImage] = useState(false);

  const images = imageUrls.length > 0 ? imageUrls : (imageUrl ? [imageUrl] : []);
  const hasImages = images.length > 0;

  const saveProductImage = async (file: File) => {
    if (uploadingImage) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    const res = await addProductImage(productId, formData);
    if (res && !res.error) {
      toast.success('Image ajoutée avec succès');
      onImagesUpdate?.();
    } else {
      toast.error(res?.error || 'Erreur lors de l\'ajout de l\'image');
    }
    setUploadingImage(false);
  };

  const removeProductImage = async (imageUrl: string, index: number) => {
    const res = await deleteProductImage(productId, { imageUrl, index });
    if (res && !res.error) {
      toast.success('Image supprimée avec succès');
      onImagesUpdate?.();
    } else {
      toast.error(res?.error || 'Erreur lors de la suppression de l\'image');
    }
  };

  return (
    <Card extra="mt-3 px-4 py-4 border">
      {hasImages ? (
        <>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-navy-700 dark:text-white">
              Galerie d'images ({images.length} image{images.length > 1 ? 's' : ''})
            </h4>
            <div className="flex items-center gap-2">
              <FileSelector
                description=""
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) saveProductImage(file);
                }}
                logo={null}
              />
              {uploadingImage && (
                <div className="text-sm text-toola-500 font-medium">
                  Téléchargement...
                </div>
              )}
            </div>
          </div>

          {/* Image Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
            {images.map((img, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                  <img
                    src={img}
                    alt={`Produit ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                  
                  {/* Delete button overlay */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={() => removeProductImage(img, index)}
                      className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg"
                      title="Supprimer cette image"
                    >
                      <MdClose className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Main image badge - only for first image */}
                  {index === 0 && (
                    <div className="absolute bottom-2 left-2 bg-toola-500 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1 shadow-lg">
                      <MdStar className="w-3 h-3" />
                      Principal
                    </div>
                  )}

                  {/* Image order indicator */}
                  <div className="absolute top-2 left-2 bg-black/50 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                    {index + 1}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="text-center">
            <h4 className="text-lg font-medium mb-2 text-navy-700 dark:text-white">
              Ajouter des images au produit
            </h4>
          </div>

          {/* Empty state upload section */}
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8">
            <div className="text-center">
              <MdCloudUpload className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h5 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Télécharger votre première image
              </h5>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                SVG, PNG, JPG ou GIF (MAX. 5MB)
              </p>
              
              <div className="flex justify-center">
                <FileSelector
                  description="Choisir un fichier"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) saveProductImage(file);
                  }}
                  logo={null}
                />
              </div>
              
              {uploadingImage && (
                <div className="mt-4 text-sm text-toola-500 font-medium">
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-toola-500"></div>
                    Téléchargement en cours...
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default ProductImageWidget;